import { request } from '@mairistem/tools';

// La passerelle retire /api/finances/pes ; les routes de l'API commencent par /pes.
const BASE_URL = '/api/finances/pes/pes';
const DELAI_SUIVI = 2000;

export interface PesMessage {
	code: string;
	message: string;
}

/** Erreur bloquante renvoyée par l'API PES, avec la liste de ses messages. */
export class PesErreur extends Error {
	messages: string[];

	constructor(messages: string[]) {
		super(messages.join('\n'));
		this.messages = messages;
	}
}

export interface FluxPes {
	nomFichier: string;
	conforme: boolean;
	anomalies: PesMessage[];
	avertissements: PesMessage[];
	contenuBase64: string;
}

interface Traitement {
	identifiant: number;
	statut: 'EN_COURS' | 'TERMINE' | 'ECHEC';
	resultat: {
		nomFichier?: string;
		conforme?: boolean;
		anomalies?: PesMessage[];
		avertissements?: PesMessage[];
		erreurs?: PesMessage[];
	} | null;
}

interface FluxPesResponse {
	status: number;
	data: unknown;
}

export interface FluxPesGenere {
	nomFichier: string;
	conforme: boolean;
	anomalies: PesMessage[];
	avertissements: PesMessage[];
	fichier: Blob;
}

const attendre = (ms: number) =>
	new Promise((resolve) => {
		setTimeout(resolve, ms);
	});

const base64ToBlob = (base64: string) => {
	const binaire = atob(base64);
	const octets = new Uint8Array(binaire.length);
	for (let i = 0; i < binaire.length; i += 1) {
		octets[i] = binaire.charCodeAt(i);
	}
	return new Blob([octets], { type: 'application/xml' });
};

export const telechargerFichier = (fichier: Blob, nomFichier: string) => {
	const url = URL.createObjectURL(fichier);
	const link = document.createElement('a');
	link.href = url;
	link.download = nomFichier;
	document.body.appendChild(link);
	link.click();
	link.remove();
	URL.revokeObjectURL(url);
};

const suivreTraitement = async (
	identifiant: number,
	headers: Record<string, string>,
): Promise<FluxPesGenere> => {
	const url = `${BASE_URL}/traitements/${identifiant}`;
	for (;;) {
		const traitement = await request.get<unknown, Traitement>(url, {
			headers,
			signal: new AbortController().signal,
		});
		if (traitement.statut === 'ECHEC') {
			throw new PesErreur(
				traitement.resultat?.erreurs?.map(({ message }) => message) ?? [],
			);
		}
		if (traitement.statut === 'TERMINE') {
			const { data } = await request.custom<unknown, { data: Blob }>(
				'get',
				`${url}/fichier`,
				undefined,
				{ headers, responseType: 'blob', signal: new AbortController().signal },
			);
			return {
				nomFichier: traitement.resultat?.nomFichier ?? 'pes.xml',
				conforme: traitement.resultat?.conforme ?? true,
				anomalies: traitement.resultat?.anomalies ?? [],
				avertissements: traitement.resultat?.avertissements ?? [],
				fichier: data,
			};
		}
		await attendre(DELAI_SUIVI);
	}
};

/**
 * Génère le flux PES_Aller d'un bordereau historisé.
 * Au-delà du seuil de pièces, l'API répond 202 : le traitement est alors suivi
 * jusqu'à sa fin puis le fichier est récupéré.
 */
export const genererFluxPesBordereau = async (
	identifiantBordereau: number,
	identifiantEntite: number | undefined,
	parametres: { mode360?: boolean } = {},
): Promise<FluxPesGenere> => {
	const headers: Record<string, string> = identifiantEntite
		? { 'X-Entite': identifiantEntite.toString() }
		: {};

	let response: FluxPesResponse;
	try {
		response = await request.custom<unknown, FluxPesResponse>(
			'post',
			`${BASE_URL}/aller/bordereaux/${identifiantBordereau}`,
			parametres,
			{ headers, signal: new AbortController().signal },
		);
	} catch (error) {
		// 202 n'est pas accepté par request : la réponse est dans l'erreur axios.
		const errorResponse = (error as { response?: FluxPesResponse }).response;
		if (errorResponse?.status !== 202) {
			if (Array.isArray(errorResponse?.data)) {
				throw new PesErreur(
					(errorResponse.data as PesMessage[]).map(({ message }) => message),
				);
			}
			throw error;
		}
		response = errorResponse;
	}

	if (response.status === 202) {
		const { identifiant } = response.data as Traitement;
		return suivreTraitement(identifiant, headers);
	}

	const flux = response.data as FluxPes;
	return {
		nomFichier: flux.nomFichier,
		conforme: flux.conforme,
		anomalies: flux.anomalies ?? [],
		avertissements: flux.avertissements ?? [],
		fichier: base64ToBlob(flux.contenuBase64),
	};
};
